/**
 * シーンの構築（three.js に触れる配線層）。
 *
 * 約束: z=0 の平面で 1 world unit = 1 CSS px（units.ts）。
 * 本（bookGroup）にズーム・パンを掛け、光と影はワールドに置いて見えている本の範囲に合わせる。
 */
import {
  AmbientLight,
  DirectionalLight,
  Group,
  Mesh,
  MeshPhongMaterial,
  PCFShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  ShadowMaterial,
  WebGLRenderer,
} from 'three';
import {
  AMBIENT_INTENSITY,
  DETAIL_PLANE_Z,
  DIRECTIONAL_INTENSITY,
  FOV_DEG,
  SHADOW_MAP_SIZE,
  SHADOW_OPACITY,
  SHADOW_PLANE_Z,
} from './constants';
import { cameraDistance, pixelRatio } from './units';

/** ページ 1 面（左・右の固定ページ）。 */
export interface PageMesh {
  readonly mesh: Mesh;
  readonly material: MeshPhongMaterial;
  /**
   * 拡大時に可視領域だけを高解像度で焼いて重ねる面（既定は非表示）。
   * ページ本体のテクスチャ（下地）には手を触れないので、visible を落とせば
   * 必ず正しい絵に戻る — めくり中の事故を構造的に防ぐ。
   */
  readonly detail: Mesh;
  readonly detailMaterial: MeshPhongMaterial;
}

export interface SceneBundle {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly book: Group;
  readonly left: PageMesh;
  readonly right: PageMesh;
  readonly light: DirectionalLight;
  /** めくり中のシートが落とす影の受け面。 */
  readonly shadow: Mesh;
  dispose(): void;
}

function createPage(): PageMesh {
  const material = new MeshPhongMaterial({ color: 0xffffff, shininess: 4 });
  const mesh = new Mesh(new PlaneGeometry(1, 1), material);
  // 影はページ自身ではなく専用の受け面（ShadowMaterial）で受ける。ページの明るさを
  // 環境光のバランスに左右されず一定に保ちつつ、投影影だけを重ねられる。
  mesh.receiveShadow = false;
  mesh.visible = false;

  // 詳細面はページと同じ材質（同じ法線・同じ陰影）なので、重ねても継ぎ目が出ない。
  // ページ面の子にはしない — 親の scale（ページ寸法）が伝播してしまうため、
  // 本グループ直下に置き、位置と寸法はエンジンが領域から計算して与える。
  const detailMaterial = new MeshPhongMaterial({ color: 0xffffff, shininess: 4 });
  const detail = new Mesh(new PlaneGeometry(1, 1), detailMaterial);
  detail.receiveShadow = false;
  detail.castShadow = false;
  detail.visible = false;
  detail.position.z = DETAIL_PLANE_Z;

  return { mesh, material, detail, detailMaterial };
}

/** レンダラ・カメラ・ライト・左右ページを組み立てる。 */
export function createScene(canvas: HTMLCanvasElement, view: Window): SceneBundle {
  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setClearAlpha(0);
  renderer.setPixelRatio(pixelRatio(view));
  renderer.shadowMap.enabled = true;
  // three 0.185 で PCFSoftShadowMap は廃止され、指定しても内部で PCFShadowMap へ
  // 落ちたうえ警告が毎回コンソールへ出る。実挙動は変わらないので明示的にこちらを指す。
  renderer.shadowMap.type = PCFShadowMap;
  // 影マップの再計算は「絵が変わるフレーム」だけ（静止中は据え置く）。
  renderer.shadowMap.autoUpdate = false;
  renderer.shadowMap.needsUpdate = true;

  const scene = new Scene();
  const camera = new PerspectiveCamera(FOV_DEG, 1, 1, 100_000);
  camera.position.set(0, 0, cameraDistance(1));

  const book = new Group();
  const left = createPage();
  const right = createPage();

  // 影の受け面: ページのすぐ手前に置いた透明な板。めくり中のシートが落とす影だけが
  // ここに描かれ、ページの上に重なって見える（ShadowMaterial は影以外は完全に透明）。
  const shadowMaterial = new ShadowMaterial({ opacity: SHADOW_OPACITY, transparent: true });
  const shadow = new Mesh(new PlaneGeometry(1, 1), shadowMaterial);
  shadow.receiveShadow = true;
  shadow.position.z = SHADOW_PLANE_Z;
  book.add(left.mesh, right.mesh, left.detail, right.detail, shadow);

  const ambient = new AmbientLight(0xffffff, AMBIENT_INTENSITY);
  const light = new DirectionalLight(0xffffff, DIRECTIONAL_INTENSITY);
  light.castShadow = true;
  light.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
  // 受け面が平面なので自己遮蔽（アクネ）を法線方向のずらしで避ける。
  light.shadow.normalBias = 2;

  scene.add(book, ambient, light, light.target);

  return {
    renderer,
    scene,
    camera,
    book,
    left,
    right,
    light,
    shadow,
    dispose() {
      left.mesh.geometry.dispose();
      right.mesh.geometry.dispose();
      left.detail.geometry.dispose();
      right.detail.geometry.dispose();
      shadow.geometry.dispose();
      shadowMaterial.dispose();
      left.material.dispose();
      right.material.dispose();
      left.detailMaterial.dispose();
      right.detailMaterial.dispose();
      renderer.dispose();
      // GL コンテキストを明示的に手放す（dispose 後の残留ゼロ・M2/M3 verify）。
      renderer.forceContextLoss();
    },
  };
}

/**
 * コンテナ寸法とページ箱に合わせて、カメラ・レンダラ・ライト・影の範囲を更新する。
 * @param box 1 ページ分の CSS ピクセル寸法
 * @param halves 見開きなら 2・単ページなら 1
 */
export function fitScene(
  bundle: SceneBundle,
  container: { width: number; height: number },
  box: { width: number; height: number },
  halves: 1 | 2,
  view: Window,
  scale = 1,
): void {
  const { renderer, camera, light } = bundle;

  renderer.setPixelRatio(pixelRatio(view) * scale);
  renderer.setSize(container.width, container.height, false);

  camera.aspect = container.height === 0 ? 1 : container.width / container.height;
  camera.position.z = cameraDistance(container.height);
  camera.near = Math.max(1, camera.position.z * 0.05);
  camera.far = camera.position.z + Math.max(box.width, box.height) * 4;
  camera.updateProjectionMatrix();

  const bookWidth = box.width * halves;
  // 光は左上手前から。影がめくり中のシートの下へ落ちる向きにする。
  light.position.set(-bookWidth * 0.35, box.height * 1.1, bookWidth * 1.6);
  light.target.position.set(0, 0, 0);
  light.target.updateMatrixWorld();

  const shadow = light.shadow.camera;
  const margin = 1.15;
  shadow.left = (-bookWidth / 2) * margin;
  shadow.right = (bookWidth / 2) * margin;
  shadow.top = (box.height / 2) * margin;
  shadow.bottom = (-box.height / 2) * margin;
  shadow.near = 1;
  shadow.far = light.position.length() + Math.max(bookWidth, box.height) * 2;
  shadow.updateProjectionMatrix();

  // ページ面の寸法と位置（見開きは背表紙を中心に左右へ、単ページは中央に置く）。
  const offset = halves === 2 ? box.width / 2 : 0;
  for (const [page, sign] of [
    [bundle.left, -1],
    [bundle.right, 1],
  ] as const) {
    page.mesh.scale.set(box.width, box.height, 1);
    page.mesh.position.set(sign * offset, 0, 0);
  }

  // 影の受け面は本の見開き全体を覆う。
  bundle.shadow.scale.set(bookWidth, box.height, 1);
}

/** 描画解像度だけを切り替える（めくり中は落とし、止まったら戻す）。 */
export function setRenderScale(
  bundle: SceneBundle,
  container: { width: number; height: number },
  view: Window,
  scale: number,
): void {
  bundle.renderer.setPixelRatio(pixelRatio(view) * scale);
  bundle.renderer.setSize(container.width, container.height, false);
}
